'use client';

import { Content, asText } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { FC, ReactNode } from 'react';
import { PrismicRichText } from '@prismicio/react';

const HighlightedText: FC<{ text: string; cardIndex: number }> = ({
  text,
  cardIndex,
}) => {
  const words = text.split(' ');
  const conjunctions = new Set(['and', 'or', 'but', 'for', 'nor', 'so', 'yet']);

  const qualifyingWords = words
    .map((word, index) => ({
      word: word.replace(/[.,]/g, ''),
      index,
    }))
    .filter((item) => item.word.length > 3);

  const highlightedIndices = new Set();
  let highlightedCount = 0;
  let i = cardIndex % 2 === 0 ? 0 : qualifyingWords.length - 1;
  const step = cardIndex % 2 === 0 ? 1 : -1;

  while (highlightedCount < 3 && i >= 0 && i < qualifyingWords.length) {
    const currentWord = qualifyingWords[i];
    highlightedIndices.add(currentWord.index);
    if (!conjunctions.has(currentWord.word.toLowerCase())) {
      highlightedCount++;
    }
    i += step;
  }

  return (
    <p className="text-2xl font-medium leading-8">
      {words.map((word, i) => (
        <span
          key={i}
          className={highlightedIndices.has(i) ? 'text-[#05F273]' : ''}
        >
          {word}{' '}
        </span>
      ))}
    </p>
  );
};

export type HeadlineWithHighlightsProps =
  SliceComponentProps<Content.HeadlineWithHighlightsSlice>;

const HeadlineWithHighlights: FC<HeadlineWithHighlightsProps> = ({ slice }) => {
  const components = {
    heading1: ({ children }: { children: ReactNode }) => (
      <h1 className="font-medium leading-[1.2] text-left m-0 p-0 text-2xl md:text-3xl lg:text-4xl">
        {children}
      </h1>
    ),
    heading2: ({ children }: { children: ReactNode }) => (
      <h2 className="font-medium leading-[1.2] text-left m-0 p-0 text-xl md:text-2xl lg:text-3xl">
        {children}
      </h2>
    ),
  };
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="pt-[70px] pb-10 md:pb-14 text-white text-center bg-highlights-gradient"
    >
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto text-left">
          <div className="mb-[60px]">
            <PrismicRichText
              field={slice.primary.headline}
              components={components}
            />
            <div className="mt-4 text-lg leading-7 text-[#c4c4c4]">
              <PrismicRichText
                field={slice.primary.description}
                components={{
                  paragraph: ({ children }) => (
                    <p className="m-0 p-0">{children}</p>
                  ),
                }}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {slice.primary.highlights.map((item, index) => (
              <div
                key={index}
                className="p-8 rounded-2xl text-left backdrop-blur-[10px]"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0) 100%)',
                  boxShadow: '0px 4.325px 64.871px 0px rgba(0, 0, 0, 0.08)',
                }}
              >
                <div className="text-white">
                  {item.highlight_text && (
                    <HighlightedText
                      text={asText(item.highlight_text) || ''}
                      cardIndex={index}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeadlineWithHighlights;
